import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ArrowLeft, 
  Edit, 
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  Building2,
  User,
  Calendar,
  CreditCard,
  FileText,
  Banknote,
  MessageSquare,
  Send,
  Loader2,
  Plus,
  Minus
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExpenseDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  const [expense, setExpense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Comments state
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);

  // Mark as Paid modal state (now supports partial payments)
  const [paidModalOpen, setPaidModalOpen] = useState(false);
  const [paymentModes, setPaymentModes] = useState([]);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [fundBalances, setFundBalances] = useState({});
  
  // Payment entries for partial/split payments
  const [paymentEntries, setPaymentEntries] = useState([{ amount: '', mode: '', date: new Date().toISOString().split('T')[0], accountType: '' }]);

  // Delete payment
  const [deletePaymentDialog, setDeletePaymentDialog] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [deletingPayment, setDeletingPayment] = useState(false);

  const canManage = user?.role === 'ADMIN' || user?.role === 'ACCOUNTANT';
  const canDelete = user?.role === 'ADMIN';

  // Fetch payment modes
  const fetchPaymentModes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/master-lists`, {
        headers: getAuthHeaders()
      });
      const paymentModesList = response.data.find(list => list.list_type === 'payment_modes');
      if (paymentModesList) {
        setPaymentModes(paymentModesList.items.filter(item => item.is_active));
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch payment modes:', error);
    }
  }, [getAuthHeaders]);

  // Fetch fund balances
  const fetchFundBalances = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fund-accounts`, {
        headers: getAuthHeaders()
      });
      const balances = {};
      response.data.forEach(account => {
        balances[account.account_type] = account.current_balance;
      });
      setFundBalances(balances);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch fund balances:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchExpense();
    fetchComments();
    fetchPaymentModes();
  }, [id, fetchPaymentModes]);

  const fetchExpense = async () => {
    try {
      const response = await axios.get(`${API}/expenses/${id}`, {
        headers: getAuthHeaders()
      });
      setExpense(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch expense:', error);
      toast.error('Failed to load expense details');
      navigate('/expenses');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      setCommentsLoading(true);
      const response = await axios.get(`${API}/expenses/${id}/comments`, {
        headers: getAuthHeaders()
      });
      setComments(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch comments:', error);
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    
    try {
      setSubmittingComment(true);
      await axios.post(`${API}/expenses/${id}/comments`, {
        content: newComment.trim()
      }, {
        headers: getAuthHeaders()
      });
      setNewComment('');
      toast.success('Comment added');
      fetchComments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`${API}/expenses/${id}/comments/${commentId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Comment deleted');
      fetchComments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete comment');
    }
  };

  const handleStatusChange = async (newStatus) => {
    // If marking as paid or adding payment, open the modal
    if (newStatus === 'PAID' || newStatus === 'ADD_PAYMENT') {
      // Calculate remaining amount
      const expenseAmount = expense?.payment_info?.amount_to_pay_inr || expense?.total_expenses || 0;
      const existingPayments = expense?.payments || [];
      const paidSoFar = existingPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
      const remaining = Math.max(0, expenseAmount - paidSoFar);
      
      // Initialize with remaining amount
      setPaymentEntries([{ 
        amount: remaining.toString(), 
        mode: '', 
        date: new Date().toISOString().split('T')[0] 
      }]);
      fetchFundBalances();
      setPaidModalOpen(true);
      return;
    }
    
    try {
      await axios.patch(`${API}/expenses/${id}/status`, {
        payment_status: newStatus
      }, {
        headers: getAuthHeaders()
      });
      toast.success(`Status updated to ${newStatus}`);
      fetchExpense();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleAddPaymentEntry = () => {
    setPaymentEntries([...paymentEntries, { amount: '', mode: '', date: new Date().toISOString().split('T')[0] }]);
  };

  const handleRemovePaymentEntry = (index) => {
    if (paymentEntries.length > 1) {
      setPaymentEntries(paymentEntries.filter((_, i) => i !== index));
    }
  };

  const handlePaymentEntryChange = (index, field, value) => {
    const updated = [...paymentEntries];
    updated[index][field] = value;
    setPaymentEntries(updated);
  };

  const handleSubmitPayments = async () => {
    // Validate entries
    const validEntries = paymentEntries.filter(e => e.amount && parseFloat(e.amount) > 0 && e.mode);
    if (validEntries.length === 0) {
      toast.error('Please add at least one valid payment');
      return;
    }
    
    // Calculate total being paid
    const totalBeingPaid = validEntries.reduce((sum, e) => sum + parseFloat(e.amount), 0);
    const expenseAmount = expense?.payment_info?.amount_to_pay_inr || expense?.total_expenses || 0;
    const existingPayments = expense?.payments || [];
    const paidSoFar = existingPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
    const remainingAmount = Math.max(0, expenseAmount - paidSoFar);
    
    // Prevent overpayment
    if (totalBeingPaid > remainingAmount) {
      toast.error(`Total payment (${formatCurrency(totalBeingPaid)}) exceeds remaining amount (${formatCurrency(remainingAmount)})`);
      return;
    }
    
    try {
      setMarkingPaid(true);
      const payments = validEntries.map(e => ({
        amount: parseFloat(e.amount),
        mode_of_payment: e.mode,
        payment_date: e.date,
        account_type: e.accountType || ''
      }));
      
      await axios.post(`${API}/expenses/${id}/payments`, {
        payments
      }, {
        headers: getAuthHeaders()
      });
      
      toast.success(`Added ${payments.length} payment(s)`);
      setPaidModalOpen(false);
      setPaymentEntries([{ amount: '', mode: '', date: new Date().toISOString().split('T')[0], accountType: '' }]);
      fetchExpense();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add payments');
    } finally {
      setMarkingPaid(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/expenses/${id}`, {
        headers: getAuthHeaders()
      });
      toast.success('Expense deleted');
      navigate('/expenses');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete expense');
    }
  };

  const handleDeletePayment = async () => {
    if (!paymentToDelete) return;
    try {
      setDeletingPayment(true);
      await axios.delete(`${API}/expenses/${id}/payments/${paymentToDelete.id}`, {
        headers: getAuthHeaders()
      });
      toast.success('Payment deleted and fund transaction reversed');
      setDeletePaymentDialog(false);
      setPaymentToDelete(null);
      fetchExpense();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete payment');
    } finally {
      setDeletingPayment(false);
    }
  };


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { class: 'badge-pending', label: 'Pending', icon: Clock },
      APPROVED: { class: 'badge-approved', label: 'Approved', icon: CheckCircle },
      PARTIALLY_PAID: { class: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Partially Paid', icon: CreditCard },
      PAID: { class: 'badge-paid', label: 'Paid', icon: CheckCircle },
      REJECTED: { class: 'badge-rejected', label: 'Rejected', icon: XCircle },
      ON_HOLD: { class: 'badge-on-hold', label: 'On Hold', icon: Clock }
    };
    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;
    return (
      <Badge className={`${config.class} font-mono text-sm px-3 py-1 flex items-center gap-2`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="expense-detail-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-950"></div>
      </div>
    );
  }

  if (!expense) {
    return null;
  }

  const expenseHeads = expense.expense_heads || {};
  const paymentInfo = expense.payment_info || {};

  return (
    <div className="space-y-6" data-testid="expense-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/expenses')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold font-heading text-indigo-950">
                Expense Details
              </h1>
              {getStatusBadge(paymentInfo.payment_status)}
            </div>
            <p className="text-slate-500 text-sm mt-1 font-mono">ID: {expense.id.slice(0, 8)}...</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/expenses/${id}/edit`)} data-testid="edit-btn">
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
          {canDelete && (
            <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)} data-testid="delete-btn">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Project Information */}
          {expense.expense_type === 'CORPORATE_TRAINING' && (
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-heading text-indigo-950 flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Training Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Client Name</p>
                    <p className="font-semibold text-slate-900 text-lg">{expense.company_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Training Name</p>
                    <p className="font-semibold text-slate-900">{expense.training_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Zoho CRM ID</p>
                    <p className="font-mono font-semibold text-indigo-950 text-lg">{expense.corporate_project_id_new || expense.corporate_project_id || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Expense ID (Zoho)</p>
                    <p className="font-mono text-slate-700">{expense.zoho_form_entry_id || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Expense Breakdown */}
          {expense.expense_type === 'CORPORATE_TRAINING' && (
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-heading text-indigo-950 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Expense Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[
                    { label: 'Trainer Cost', value: expenseHeads.trainer_cost },
                    { label: 'Books', value: expenseHeads.books },
                    { label: 'Trainer Accommodation', value: expenseHeads.trainer_accommodation },
                    { label: 'Client Expense', value: expenseHeads.client_expense },
                    { label: 'Trainer Travel', value: expenseHeads.trainer_travel },
                    { label: 'Trainer Food', value: expenseHeads.trainer_food },
                    { label: 'Other Expenses', value: expenseHeads.other_expenses },
                  ].map((item, index) => (
                    <div key={item.label} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                      <span className="text-slate-600">{item.label}</span>
                      <span className="font-mono font-medium text-slate-900">{formatCurrency(item.value)}</span>
                    </div>
                  ))}
                  <Separator className="my-2" />
                  <div className="flex justify-between items-center py-2">
                    <span className="font-semibold text-indigo-950">Total Expenses</span>
                    <span className="font-mono font-bold text-xl text-indigo-950">{formatCurrency(expense.total_expenses)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Ad-hoc Expense Details */}
          {expense.expense_type !== 'CORPORATE_TRAINING' && (
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-heading text-indigo-950 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Expense Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Expense Type</p>
                    <Badge variant="outline" className="mt-1">{expense.expense_type?.replace('_', ' ')}</Badge>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Amount</p>
                    <p className="font-mono font-bold text-xl text-indigo-950">{formatCurrency(expense.total_expenses)}</p>
                  </div>
                </div>
                {expense.description && (
                  <div>
                    <p className="text-sm text-slate-500">Description</p>
                    <p className="text-slate-900 mt-1">{expense.description}</p>
                  </div>
                )}
                {expense.vendor_name && (
                  <div>
                    <p className="text-sm text-slate-500">Vendor</p>
                    <p className="font-semibold text-slate-900">{expense.vendor_name}</p>
                  </div>
                )}
                {expense.invoice_number && (
                  <div>
                    <p className="text-sm text-slate-500">Invoice Number</p>
                    <p className="font-mono text-slate-900">{expense.invoice_number}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Payment Information */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-heading text-indigo-950 flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Payment Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Payment Type</p>
                  <p className="font-medium text-slate-900">{paymentInfo.payment_type?.replace('_', ' ') || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Amount to Pay</p>
                  <p className="font-mono font-semibold text-indigo-950">{formatCurrency(paymentInfo.amount_to_pay_inr)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Expected Payment Date</p>
                  <p className="font-mono text-slate-900">{paymentInfo.expected_payment_date || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Mode of Payment</p>
                  <p className="font-medium text-slate-900">{paymentInfo.mode_of_payment || '-'}</p>
                </div>
              </div>
              
              {/* Show actual payment date when paid */}
              {paymentInfo.payment_status === 'PAID' && paymentInfo.actual_payment_date && (
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <CheckCircle className="w-4 h-4" />
                    <span className="font-medium">Paid on {paymentInfo.actual_payment_date}</span>
                    {paymentInfo.mode_of_payment && (
                      <Badge className="bg-emerald-100 text-emerald-800 border-0">
                        via {paymentInfo.mode_of_payment}
                      </Badge>
                    )}
                  </div>
                </div>
              )}
              
              {paymentInfo.account_details && (
                <div>
                  <p className="text-sm text-slate-500">Account Details</p>
                  <p className="text-slate-900 font-mono text-sm bg-slate-50 p-3 rounded-lg mt-1">
                    {paymentInfo.account_details}
                  </p>
                </div>
              )}
              {paymentInfo.transaction_reference && (
                <div>
                  <p className="text-sm text-slate-500">Transaction Reference</p>
                  <p className="font-mono text-slate-900">{paymentInfo.transaction_reference}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment History (for split/partial payments) */}
          {expense?.payments && expense.payments.length > 0 && (
            <Card className="border-slate-200" data-testid="payment-history">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-heading text-indigo-950 flex items-center gap-2">
                  <Banknote className="w-5 h-5" />
                  Payment History ({expense.payments.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {expense.payments.map((payment, idx) => (
                    <div key={payment.id || idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-mono font-semibold text-slate-900">{formatCurrency(payment.amount)}</p>
                          <p className="text-xs text-slate-500">
                            via {payment.mode_of_payment}{payment.account_type ? ` (${payment.account_type})` : ''} • {payment.payment_date}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right text-xs text-slate-400">
                          {payment.created_by && <p>by {payment.created_by}</p>}
                        </div>
                        {canManage && (
                          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-7 w-7 p-0"
                            onClick={() => { setPaymentToDelete(payment); setDeletePaymentDialog(true); }}
                            data-testid={`delete-payment-${payment.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  <Separator className="my-3" />
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Total Paid</span>
                    <span className="font-mono font-bold text-emerald-600">
                      {formatCurrency(expense.payments.reduce((sum, p) => sum + (p.amount || 0), 0))}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Remaining</span>
                    <span className="font-mono font-bold text-slate-900">
                      {formatCurrency(Math.max(0, (expense?.payment_info?.amount_to_pay_inr || expense?.total_expenses || 0) - expense.payments.reduce((sum, p) => sum + (p.amount || 0), 0)))}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Comments Section */}
          <Card className="border-slate-200" data-testid="comments-section">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-heading text-indigo-950 flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Comments ({comments.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add Comment Form */}
              <form onSubmit={handleAddComment} className="space-y-3">
                <Textarea
                  placeholder="Add a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="min-h-[80px] resize-none"
                  data-testid="comment-input"
                />
                <div className="flex justify-end">
                  <Button 
                    type="submit" 
                    disabled={!newComment.trim() || submittingComment}
                    className="bg-indigo-950 hover:bg-indigo-900"
                    data-testid="submit-comment-btn"
                  >
                    {submittingComment ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Post Comment
                  </Button>
                </div>
              </form>

              <Separator />

              {/* Comments List */}
              {commentsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : comments.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <MessageSquare className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p>No comments yet. Be the first to add one!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {comments.map((comment) => (
                    <div key={comment.id} className="flex gap-3" data-testid={`comment-${comment.id}`}>
                      <Avatar className="w-8 h-8 flex-shrink-0">
                        <AvatarFallback className="bg-indigo-100 text-indigo-700 text-xs font-semibold">
                          {comment.user_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-900 text-sm">{comment.user_name}</span>
                          <Badge variant="outline" className="text-xs px-1.5 py-0">
                            {comment.user_role === 'KAM' ? 'Key Account Manager' : comment.user_role}
                          </Badge>
                          <span className="text-xs text-slate-400">
                            {new Date(comment.created_at).toLocaleString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                        <p className="text-slate-700 text-sm mt-1 whitespace-pre-wrap">{comment.content}</p>
                        {(user?.role === 'ADMIN' || user?.id === comment.user_id) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-6 px-2 mt-1"
                            onClick={() => handleDeleteComment(comment.id)}
                            data-testid={`delete-comment-${comment.id}`}
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          {canManage && (
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-heading text-indigo-950">Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {/* Approve - only when Pending */}
                {paymentInfo.payment_status === 'PENDING' && (
                  <Button 
                    className="w-full justify-start bg-emerald-600 hover:bg-emerald-700" 
                    onClick={() => handleStatusChange('APPROVED')}
                    data-testid="approve-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve
                  </Button>
                )}
                {/* Mark as Paid / Add Payment - when Approved or Partially Paid */}
                {(paymentInfo.payment_status === 'APPROVED' || paymentInfo.payment_status === 'PARTIALLY_PAID') && (
                  <Button 
                    className="w-full justify-start bg-blue-600 hover:bg-blue-700" 
                    onClick={() => handleStatusChange('ADD_PAYMENT')}
                    data-testid="mark-paid-btn"
                  >
                    <Banknote className="w-4 h-4 mr-2" />
                    {paymentInfo.payment_status === 'PARTIALLY_PAID' ? 'Add Payment' : 'Mark as Paid'}
                  </Button>
                )}
                {/* Reject - only when Pending */}
                {paymentInfo.payment_status === 'PENDING' && (
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-red-600 border-red-200 hover:bg-red-50" 
                    onClick={() => handleStatusChange('REJECTED')}
                    data-testid="reject-btn"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                )}
                {/* Show partial payment info */}
                {paymentInfo.payment_status === 'PARTIALLY_PAID' && (
                  <div className="p-3 bg-blue-50 rounded-lg text-blue-700 text-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <CreditCard className="w-4 h-4" />
                      <span className="font-medium">Partially Paid</span>
                    </div>
                    <div className="text-xs space-y-1">
                      <p>Paid: {formatCurrency((expense?.payments || []).reduce((sum, p) => sum + (p.amount || 0), 0))}</p>
                      <p>Remaining: {formatCurrency(Math.max(0, (expense?.payment_info?.amount_to_pay_inr || expense?.total_expenses || 0) - (expense?.payments || []).reduce((sum, p) => sum + (p.amount || 0), 0)))}</p>
                    </div>
                  </div>
                )}
                {/* Show status message for terminal states */}
                {paymentInfo.payment_status === 'PAID' && (
                  <div className="p-3 bg-emerald-50 rounded-lg text-emerald-700 text-sm flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    This expense has been fully paid
                  </div>
                )}
                {paymentInfo.payment_status === 'REJECTED' && (
                  <div className="p-3 bg-red-50 rounded-lg text-red-700 text-sm flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    This expense was rejected
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Metadata */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-heading text-indigo-950">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Date of Report</p>
                  <p className="font-mono text-sm">{expense.reporting_date || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <User className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Submitted By</p>
                  <p className="text-sm font-medium">{expense.submitted_by || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <FileText className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Source</p>
                  <Badge variant="outline" className={expense.source === 'ZOHO_WEBHOOK' ? 'border-emerald-300 text-emerald-700 bg-emerald-50' : 'border-slate-300'}>
                    {expense.source === 'ZOHO_WEBHOOK' ? 'Zoho' : 'Manual'}
                  </Badge>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Created At</p>
                  <p className="font-mono text-sm">{expense.created_at?.split('T')[0]}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Expense</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this expense? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Payment Confirmation */}
      <AlertDialog open={deletePaymentDialog} onOpenChange={setDeletePaymentDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Payment?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this payment of {formatCurrency(paymentToDelete?.amount)}
              {paymentToDelete?.account_type ? ` from ${paymentToDelete.account_type}` : ''} and reverse the fund transaction.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePayment} disabled={deletingPayment} className="bg-red-600 hover:bg-red-700">
              {deletingPayment ? 'Deleting...' : 'Delete Payment'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mark as Paid Modal (supports partial/split payments) */}
      <Dialog open={paidModalOpen} onOpenChange={setPaidModalOpen}>
        <DialogContent className="sm:max-w-[500px]" data-testid="mark-paid-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-600" />
              {expense?.payment_info?.payment_status === 'PARTIALLY_PAID' ? 'Add Payment' : 'Add Payment(s)'}
            </DialogTitle>
            <DialogDescription>
              Add one or more payments. You can split across different payment modes.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Expense Summary */}
            <div className="p-3 bg-slate-50 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Total Amount</span>
                <span className="font-mono font-bold text-indigo-950">
                  {formatCurrency(expense?.payment_info?.amount_to_pay_inr || expense?.total_expenses)}
                </span>
              </div>
              {expense?.payments && expense.payments.length > 0 && (
                <>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500">Already Paid</span>
                    <span className="font-mono font-semibold text-emerald-600">
                      {formatCurrency((expense?.payments || []).reduce((sum, p) => sum + (p.amount || 0), 0))}
                    </span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-slate-700">Remaining</span>
                    <span className="font-mono font-bold text-indigo-950">
                      {formatCurrency(Math.max(0, (expense?.payment_info?.amount_to_pay_inr || expense?.total_expenses || 0) - (expense?.payments || []).reduce((sum, p) => sum + (p.amount || 0), 0)))}
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Payment Entries */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Payment Entries</Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={handleAddPaymentEntry}
                  className="h-7 text-xs"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add Split
                </Button>
              </div>
              
              {paymentEntries.map((entry, index) => (
                <div key={`payment-entry-${index}`} className="p-3 border border-slate-200 rounded-lg space-y-3 relative">
                  {paymentEntries.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute top-1 right-1 h-6 w-6 p-0 text-slate-400 hover:text-red-500"
                      onClick={() => handleRemovePaymentEntry(index)}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                  )}
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Amount *</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0"
                        value={entry.amount}
                        onChange={(e) => handlePaymentEntryChange(index, 'amount', e.target.value)}
                        className="h-9"
                        data-testid={`payment-amount-${index}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Date</Label>
                      <Input
                        type="date"
                        value={entry.date}
                        onChange={(e) => handlePaymentEntryChange(index, 'date', e.target.value)}
                        className="h-9"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Mode of Payment *</Label>
                    <Select 
                      value={entry.mode} 
                      onValueChange={(val) => handlePaymentEntryChange(index, 'mode', val)}
                    >
                      <SelectTrigger className="h-9" data-testid={`payment-mode-${index}`}>
                        <SelectValue placeholder="Select mode" />
                      </SelectTrigger>
                      <SelectContent>
                        {paymentModes.map((mode) => (
                          <SelectItem key={mode.value} value={mode.label}>
                            {mode.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Show balance for selected mode */}
                  {entry.mode && entry.mode !== 'Online' && (
                    <div className="text-xs text-slate-500 flex items-center justify-between pt-1 border-t border-slate-100">
                      <span>{entry.mode === 'UPI' ? 'Omnicard' : entry.mode} Balance:</span>
                      <span className="font-mono font-medium">
                        {formatCurrency(fundBalances[entry.mode === 'UPI' ? 'OMNICARD' : entry.mode.toUpperCase()] || 0)}
                      </span>
                    </div>
                  )}

                  {/* Account Type Override */}
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Fund Account (where money came from)</Label>
                    <Select
                      value={entry.accountType || ''}
                      onValueChange={(val) => handlePaymentEntryChange(index, 'accountType', val)}
                    >
                      <SelectTrigger className="h-9" data-testid={`payment-account-${index}`}>
                        <SelectValue placeholder="Auto (based on mode)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ONLINE">Online</SelectItem>
                        <SelectItem value="DEPOSIT">Deposit</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Show selected account balance */}
                  {entry.accountType && (
                    <div className="text-xs text-slate-500 flex items-center justify-between pt-1 border-t border-slate-100">
                      <span>{entry.accountType} Balance:</span>
                      <span className="font-mono font-medium">
                        {formatCurrency(fundBalances[entry.accountType] || 0)}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Total being paid */}
            <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-emerald-700">Total Being Paid</span>
                <span className="font-mono font-bold text-emerald-700">
                  {formatCurrency(paymentEntries.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0))}
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setPaidModalOpen(false)}
              data-testid="cancel-paid-btn"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitPayments}
              disabled={!paymentEntries.some(e => e.amount && parseFloat(e.amount) > 0 && e.mode) || markingPaid}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-paid-btn"
            >
              {markingPaid ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Add Payment{paymentEntries.filter(e => e.amount && e.mode).length > 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpenseDetailPage;
